/* eslint-disable react/jsx-wrap-multilines */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

/** Atoms */
import { TabPanel } from "@atoms/index";

/** Molecules */
import {
  BaseCard,
  BaseCardHeader,
  BaseCardItem,
  MessagesBox
} from "@molecules/index";
import type {
  IConcretingRequestConcreteRecipeData,
  IConcretingRequestConcretingData,
  IConcretingRequestPumpData
} from "@molecules/index";

// store
import { useAtom, WritableAtom, SetStateAction } from "@libraries/jotai";

// Organisms
import StepTabs, { IStepTabProps } from "@organisms/StepTabs";

/** Libraries */
import {
  DialogTitle,
  DialogActions,
  Button,
  Zoom,
  TransitionProps,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from "@libraries/mui";
import { Close } from "@libraries/icons";

/** Jotai */
import Store, { ContractPermissions } from "@store/jotai/index";

// graphql
import {
  ConcretingRequestType,
  EConcretingRequestStatus,
  ConcretingRequestMutationsConcretingRequestUpdateStatusArgs,
  ECommandResultStatus,
  ConcretingRequestMessageType
} from "@store/graphql/helpers/types.graphql";
import {
  concretingRequestUpdateStatus,
  TConcretingRequestUpdateStatusMutations
} from "@store/graphql/groups/concretingRequest/mutation.graphql";

// Language
import { ConcretingApprovalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

/** Styled components */
import * as Styled from "./styles";

/** Transition helper component */
const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
  ) => <Zoom ref={ref} {...props} />
);

enum EStepTabPanelIndexes {
  details = 0,
  actions = 1,
  messages = 2
}

interface CustomOptionType {
  id: string;
  value: string;
}

interface ConcretingApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  selectedStepTabAtom: WritableAtom<number, SetStateAction<number>, void>;
  concretingRequest: ConcretingRequestType;
}

const ConcretingApprovalDialog: React.FC<ConcretingApprovalDialogProps> = ({
  open,
  onClose,
  selectedStepTabAtom,
  concretingRequest
}) => {
  const { t } = useTranslation();

  const { addToast } = useToast();

  // status options
  const statusOptions: CustomOptionType[] = useMemo(
    () => [
      {
        id: EConcretingRequestStatus.PENDING,
        value: t(ConcretingApprovalMessages.concretingRequestStatusPending)
      },
      {
        id: EConcretingRequestStatus.APPROVED,
        value: t(ConcretingApprovalMessages.concretingRequestStatusApproved)
      },
      {
        id: EConcretingRequestStatus.REJECTED,
        value: t(ConcretingApprovalMessages.concretingRequestStatusRejected)
      }
    ],
    [t]
  );

  // inputs states
  // const [observation, setObservation] = useState("");
  const [status, setStatus] = useState(statusOptions[0].id);

  // handles for inputs
  // const handleObservationChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     setObservation(event.target.value);
  //   },
  //   []
  // );
  const handleStatusChange = useCallback((event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  }, []);

  const steps: IStepTabProps[] = useMemo(() => [
    {
      id: "stepsConcretingApproval_Details",
      tabLabel: t(ConcretingApprovalMessages.stepDetailsText)
    },
    {
      id: "stepsConcretingApproval_Actions",
      tabLabel: t(ConcretingApprovalMessages.stepActionsText)
    },
    {
      id: "stepsConcretingApproval_Messages",
      tabLabel: t(ConcretingApprovalMessages.stepMessagesText)
    }
  ], [t]);
  const [currentSteps, setCurrentSteps] = useState<IStepTabProps[]>(steps);
  const [selectedStepTab, setSelectedStepTab] = useAtom(selectedStepTabAtom);

  // mutation
  const [MutationConcretingRequestUpdateStatus] = useMutation<
    TConcretingRequestUpdateStatusMutations,
    ConcretingRequestMutationsConcretingRequestUpdateStatusArgs
  >(concretingRequestUpdateStatus(["id"]));

  const handleClose = useCallback(() => {
    // setObservation("");
    setStatus(statusOptions[0].id);
    onClose();
  }, [onClose, statusOptions]);

  const handleConfirm = useCallback(async () => {
    try {
      const mutation = await MutationConcretingRequestUpdateStatus({
        variables: {
          concretingRequestStatus: {
            id: concretingRequest.id,
            status: status as EConcretingRequestStatus
          }
        }
      });

      const response = mutation.data?.concretingRequest.concretingRequestUpdateStatus;

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      handleClose();

      addToast({
        type: "success",
        title: response.message
      });
    } catch (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    }
  }, [
    handleClose,
    MutationConcretingRequestUpdateStatus,
    concretingRequest,
    status,
    addToast
  ]);

  const concretingRequestDetails = useCallback(
    (concretingRequest: ConcretingRequestType) => {
      const concretingRequestConcreting: IConcretingRequestConcretingData = {
        status: concretingRequest.status,
        requestDate: concretingRequest.requestedDate,
        concreteBatchingPlantName:
          concretingRequest.concreteBatchingPlant?.name ?? "",
        // toConfirm: concretingRequest.toConfirm,
        // travelsReleased: concretingRequest.travelsReleased ?? undefined,
        interval: concretingRequest.interval,
        observationConcreting: concretingRequest.observationConcreting ?? "",
        requester: concretingRequest.requester,
        requesterPhoneNumber: concretingRequest.requesterPhoneNumber
      };

      const concretingRequestConcreteRecipe: IConcretingRequestConcreteRecipeData = {
        concreteRecipeDescription:
            concretingRequest.contractConcreteRecipe?.concreteRecipe
              ?.description ?? "",
        quantityPerConcreteMixer: concretingRequest.quantityPerConcreteMixer,
        pieceToBeConcreted: concretingRequest.pieceToBeConcreted ?? undefined,
        requestedQuantity: concretingRequest.requestedQuantity,
        status: concretingRequest.status
      };

      const concretingRequestPump: IConcretingRequestPumpData = {
        pump: concretingRequest.pump,
        pipingDistance: concretingRequest.pipingDistance,
        observationPump: concretingRequest.observationPump ?? undefined,
        status: concretingRequest.status
      };

      return (
        <div>
          <BaseCardItem
            type="concretingRequestConcreting"
            concretingRequestConcreting={concretingRequestConcreting}
          />
          <BaseCardItem
            type="concretingRequestConcreteRecipe"
            concretingRequestConcreteRecipe={concretingRequestConcreteRecipe}
          />
          <BaseCardItem
            type="concretingRequestPump"
            concretingRequestPump={concretingRequestPump}
          />
        </div>
      );
    },
    []
  );

  return (
    <Styled.Container
      open={open}
      onClose={handleClose}
      aria-labelledby="concretingApprovalDialog-title"
      aria-describedby="concretingApprovalDialog-description"
      TransitionComponent={Transition}
    >
      <DialogTitle id="concretingApprovalDialog-title">
        {t(ConcretingApprovalMessages.dialogTitleText)}
        <Styled.CustomIconButton aria-label="close" onClick={handleClose}>
          <Close />
        </Styled.CustomIconButton>
      </DialogTitle>
      <Styled.CustomDialogContent>
        <Styled.ContentContainer>
          <StepTabs
            content={currentSteps}
            selectedStepTab={selectedStepTab}
            setSelectedStepTab={setSelectedStepTab}
            mode="EDIT"
            disableControlButtons
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onFinish={() => {}}
          >
            <Styled.CustomTabPanel
              id="full-width-detailsStepTabPanel"
              value={selectedStepTab}
              index={EStepTabPanelIndexes.details}
            >
              <Styled.ConcretingRequestContent>
                <BaseCard
                  key={concretingRequest.id}
                  id={concretingRequest.id}
                  header={
                    <BaseCardHeader
                      type="concretingRequest"
                      data={concretingRequest}
                    />
                  }
                  details={concretingRequestDetails(concretingRequest)}
                />
              </Styled.ConcretingRequestContent>
            </Styled.CustomTabPanel>
            <Styled.CustomTabPanel
              id="full-width-actionsStepTabPanel"
              value={selectedStepTab}
              index={EStepTabPanelIndexes.actions}
            >
              <Styled.ActionsContent>
                <BaseCard
                  key={concretingRequest.id}
                  id={concretingRequest.id}
                  header={
                    <BaseCardHeader
                      type="concretingRequest"
                      data={concretingRequest}
                    />
                  }
                />
                <Styled.CustomFormControl
                  className="default-field"
                  size="small"
                  fullWidth
                >
                  <InputLabel id="concrete-type-select">
                    {t(ConcretingApprovalMessages.actionsStatusFieldLabel)}
                  </InputLabel>
                  <Select
                    labelId="pump-select"
                    label={t(
                      ConcretingApprovalMessages.actionsStatusFieldLabel
                    )}
                    value={status}
                    onChange={handleStatusChange}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        <Styled.SelectItem>{option.value}</Styled.SelectItem>
                      </MenuItem>
                    ))}
                  </Select>
                </Styled.CustomFormControl>

                {/* <TextField
                  autoComplete="observation"
                  label={t(
                    ConcretingApprovalMessages.actionsObservationFieldLabel
                  )}
                  type="text"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={6}
                  size="small"
                  className="default-field"
                  value={observation}
                  onChange={handleObservationChange}
                /> */}
              </Styled.ActionsContent>
            </Styled.CustomTabPanel>
            <Styled.CustomTabPanel
              id="full-width-messagesStepTabPanel"
              value={selectedStepTab}
              index={EStepTabPanelIndexes.messages}
            >
              <Styled.MessagesContent>
                <MessagesBox concretingRequestId={concretingRequest.id} />
              </Styled.MessagesContent>
            </Styled.CustomTabPanel>
          </StepTabs>
        </Styled.ContentContainer>
      </Styled.CustomDialogContent>
      {selectedStepTab !== 2 && (
        <DialogActions>
          <Styled.DialogActions>
            <Button onClick={handleConfirm}>
              {t(
                ConcretingApprovalMessages.dialogButtonSubmitText
              ).toUpperCase()}
            </Button>
          </Styled.DialogActions>
        </DialogActions>
      )}
    </Styled.Container>
  );
};

export default ConcretingApprovalDialog;
